import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ArrowLeft, 
  CheckCircle2, 
  Loader2, 
  Link2,
  Unlink,
  RefreshCw,
  ExternalLink,
  BarChart3,
  Zap
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Workspace ID do Unified.to (extraído do JWT)
const UNIFIED_WORKSPACE_ID = "6982776304222e98a09ff5c6";

const ConnectUnified = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [connections, setConnections] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  // Verificar se voltou de uma autorização OAuth
  useEffect(() => {
    const connectionId = searchParams.get('id');
    if (connectionId) {
      handleOAuthCallback(connectionId);
    } else {
      loadData();
    }
  }, [clientId, searchParams]);

  const handleOAuthCallback = async (connectionId) => {
    toast.success("Conta conectada com sucesso!");
    
    // Salvar conexão no banco
    try {
      await axios.post(`${API}/unified/connections/${clientId}/save`, {
        connection_id: connectionId
      });
    } catch (e) {
      console.log("Salvando localmente...");
    }
    
    // Limpar URL e recarregar
    window.history.replaceState({}, '', `/connect-unified/${clientId}`);
    loadData();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Verificar status
      const statusRes = await axios.get(`${API}/unified/status`);
      setStatus(statusRes.data);

      // Buscar conexões do cliente
      const connRes = await axios.get(`${API}/unified/connections/${clientId}`);
      setConnections(connRes.data.active_connections || []);

    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async (connectionId) => {
    try {
      const res = await axios.get(`${API}/unified/campaigns/${connectionId}`);
      setCampaigns(res.data.campaigns || []);
      toast.success(`${res.data.campaigns?.length || 0} campanhas encontradas!`);
    } catch (error) {
      console.error("Erro ao carregar campanhas:", error);
      toast.error("Erro ao carregar campanhas");
    }
  };

  const syncCampaigns = async (connectionId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/unified/sync/${clientId}?connection_id=${connectionId}`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      console.error("Erro ao sincronizar:", error);
      toast.error("Erro ao sincronizar campanhas");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando integrações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🔗 Conectar Plataformas de Anúncios
          </h1>
          <p className="text-gray-600">
            Conecte suas contas de Meta, Google ou TikTok Ads via Unified.to
          </p>
        </div>

        {/* Status */}
        <Alert className={`mb-6 ${status?.configured ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}`}>
          <Zap className={`h-5 w-5 ${status?.configured ? 'text-green-600' : 'text-yellow-600'}`} />
          <AlertDescription className={status?.configured ? 'text-green-800' : 'text-yellow-800'}>
            {status?.configured 
              ? "✅ Unified.to configurado e pronto para conectar suas contas!"
              : "⚠️ Unified.to não está configurado. Entre em contato com o suporte."}
          </AlertDescription>
        </Alert>

        {/* Widget de Autorização */}
        {status?.configured && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-purple-600" />
                Conectar Nova Conta
              </CardTitle>
              <CardDescription>
                Clique em uma plataforma para autorizar o acesso
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Widget Unified.to */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 min-h-[200px]">
                <div 
                  id="unified_widget" 
                  className="w-full"
                  dangerouslySetInnerHTML={{
                    __html: `
                      <script src="https://api.unified.to/docs/unified.js?wid=${UNIFIED_WORKSPACE_ID}&did=unified_widget&categories=martech&env=Production&success_redirect=${encodeURIComponent(window.location.href)}"></script>
                    `
                  }}
                />
                
                {/* Fallback manual */}
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-gray-500 text-center mb-4">
                    Ou conecte manualmente:
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button 
                      variant="outline" 
                      onClick={() => window.open(`https://embed.unified.to/auth/${UNIFIED_WORKSPACE_ID}?categories=martech&success_redirect=${encodeURIComponent(window.location.href)}`, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <img src="https://api.unified.to/images/integrations/facebook_ads.svg" alt="Meta" className="w-5 h-5" onError={(e) => e.target.style.display='none'} />
                      Meta Ads
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open(`https://embed.unified.to/auth/${UNIFIED_WORKSPACE_ID}?categories=martech&success_redirect=${encodeURIComponent(window.location.href)}`, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <img src="https://api.unified.to/images/integrations/google_ads.svg" alt="Google" className="w-5 h-5" onError={(e) => e.target.style.display='none'} />
                      Google Ads
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open(`https://embed.unified.to/auth/${UNIFIED_WORKSPACE_ID}?categories=martech&success_redirect=${encodeURIComponent(window.location.href)}`, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <img src="https://api.unified.to/images/integrations/tiktok_ads.svg" alt="TikTok" className="w-5 h-5" onError={(e) => e.target.style.display='none'} />
                      TikTok Ads
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Conexões Ativas */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Conexões Ativas
              </span>
              <Button variant="ghost" size="sm" onClick={loadData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {connections.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                Nenhuma conta conectada ainda. Use o widget acima para conectar.
              </p>
            ) : (
              <div className="space-y-3">
                {connections.map((conn, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{conn.integration_type || 'ads'}</Badge>
                      <span className="font-medium">{conn.id}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => loadCampaigns(conn.id)}
                      >
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Ver Campanhas
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => syncCampaigns(conn.id)}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Sincronizar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Campanhas Carregadas */}
        {campaigns.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>📊 Campanhas ({campaigns.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {campaigns.map((camp, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{camp.name}</p>
                      <p className="text-sm text-gray-500">
                        Status: {camp.status} | Objetivo: {camp.objective}
                      </p>
                    </div>
                    <Badge variant={camp.status === 'ACTIVE' ? 'default' : 'secondary'}>
                      {camp.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plataformas Suportadas */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>📋 Plataformas Suportadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="text-3xl mb-2">📘</div>
                <p className="font-medium">Meta Ads</p>
                <p className="text-xs text-gray-500">Facebook & Instagram</p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-3xl mb-2">🔍</div>
                <p className="font-medium">Google Ads</p>
                <p className="text-xs text-gray-500">Search & Display</p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-3xl mb-2">🎵</div>
                <p className="font-medium">TikTok Ads</p>
                <p className="text-xs text-gray-500">Video Ads</p>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default ConnectUnified;
